import { range } from "lodash";
import { FormattedNumber } from "react-intl";
import {
  Pagination,
  PaginationContent,
  PaginationButton,
  PaginationItem,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
  PaginationSpacer,
} from "@/kit/ui/pagination";

const genPages = (pageIndex: number, pageCount: number, maxDisplay: number) => {
  const displayCount = Math.min(maxDisplay, pageCount);
  const siblings = Math.floor((displayCount - 1) / 2);
  const rem = (displayCount - 1) % 2;
  const around = range(pageIndex - siblings - rem, pageIndex + siblings + 1);
  while (around[0] < 0) {
    around.shift();
    const last = around[around.length - 1];
    if (last < pageCount - 1) {
      around.push(last + 1);
    }
  }
  while (around[around.length - 1] > pageCount - 1) {
    around.pop();
    const first = around[0];
    if (first > 0) {
      around.unshift(first - 1);
    }
  }
  const pages: (number | null)[] = around;
  if (pages[0] !== 0) {
    if (siblings > 1) {
      pages[0] = 0;
      pages[1] = null;
    } else if (siblings > 0) {
      pages[0] = null;
    }
  }
  if (pages[pages.length - 1] !== pageCount - 1) {
    if (siblings > 1) {
      pages[pages.length - 1] = pageCount - 1;
      pages[pages.length - 2] = null;
    } else if (siblings > 0) {
      pages[pages.length - 1] = null;
    }
  }
  return pages;
};

export interface AutoPaginationProps {
  pageIndex: number;
  pageCount: number;
  maxDisplay?: number;
  setPageIndex: (page: number) => void;
}

export default function AutoPagination({
  pageIndex,
  pageCount,
  maxDisplay = 7,
  setPageIndex,
}: AutoPaginationProps) {
  if (pageCount < 2) {
    return null;
  }
  const pages = genPages(pageIndex, pageCount, maxDisplay);
  return (
    <Pagination>
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious
            disabled={pageIndex === 0}
            onClick={() => setPageIndex(pageIndex - 1)}
            suppressHydrationWarning
          />
        </PaginationItem>
        <PaginationSpacer />
        {pages.map((page, index) => (
          <PaginationItem key={index}>
            {page === null ? (
              <PaginationEllipsis />
            ) : (
              <PaginationButton
                isActive={pageIndex === page}
                onClick={() => {
                  setPageIndex(page);
                }}
              >
                <FormattedNumber value={page + 1} />
              </PaginationButton>
            )}
          </PaginationItem>
        ))}
        <PaginationSpacer />
        <PaginationItem>
          <PaginationNext
            disabled={pageIndex + 1 === pageCount}
            onClick={() => setPageIndex(pageIndex + 1)}
            suppressHydrationWarning
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
}
